import React, { useState } from "react";

import { strings } from "@perceptron/constants";
import { transformPlayerSrc } from "@perceptron/core";
import { cn } from "@perceptron/utils/cn";
import { getContentTypeFromExtension } from "@perceptron/utils/mime";
import { MediaPlayer, MediaProvider } from "@vidstack/react";

import { PlayGlyph } from "@demo/features/media/PlayGlyph";

import { MediaTileRemoveButton } from "./MediaTileRemoveButton";
import { BaseMediaRendererProps } from "./types";

export const VideoRenderer: React.FC<BaseMediaRendererProps> = React.memo(
  ({ media, index, onSelect, onRemove, showControls = true, className = "" }) => {
    const videoSrc = media.url || "";

    const [isDragging, setIsDragging] = useState(false);

    if (!videoSrc) {
      console.error("Video URL is missing for media:", media);
    }

    const playerSrc = {
      src: videoSrc,
      type: getContentTypeFromExtension(media.extension),
    };

    return (
      <div
        className={cn(
          "group/tile relative size-full cursor-pointer overflow-hidden rounded-md shadow-[0_4px_4px_rgba(0,0,0,0.07)]",
          className,
        )}
        onClick={(e) => onSelect?.(e, index)}
        onTouchEnd={(e) => {
          if (isDragging) {
            setIsDragging(false);
            return;
          }

          e.preventDefault();
          e.stopPropagation();

          onSelect?.(e, index);
        }}
        onTouchMove={() => {
          if (!isDragging) {
            setIsDragging(true);
          }
        }}
      >
        {showControls && <MediaTileRemoveButton index={index} testId={`delete-video-${index}`} onRemove={onRemove} />}
        <div className="relative h-full w-full overflow-hidden rounded-md">
          {videoSrc ? (
            <>
              <MediaPlayer
                className="flex h-full flex-col gap-2"
                // Match the agent-output `<MediaPlayer crossOrigin="anonymous">` so a video
                // loaded into the input area shares the same CORS-mode HTTP cache entry the
                // agent-output player will later read. Otherwise a no-cors fetch can cache a
                // response without ACAO and the CORS-mode read fails.
                crossOrigin="anonymous"
                playsInline
                src={transformPlayerSrc(playerSrc)}
              >
                <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-md bg-black">
                  <MediaProvider className="h-full w-full [&>video]:h-full [&>video]:w-full [&>video]:object-cover" />
                </div>
              </MediaPlayer>
              <PlayGlyph size={26} />
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-200 text-xs text-gray-500">
              {strings.videoRenderer.invalidVideo}
            </div>
          )}
        </div>
      </div>
    );
  },
);

VideoRenderer.displayName = "VideoRenderer";
